import type { DisruptionPnrGroupRow, RosterTransportVoucher } from '@/lib/disruptions/types';
import type { TripLeg } from './types';
import type { GroupVoucherView, VoucherScenario } from './voucher-types';

// Does the group's Uber voucher take the passengers to the hotel? (design 2026-10-21)
//
// With AUTOMATIC + the policy's voucher on, a confirmed hotel issues the voucher
// INSTEAD of a car, so "transport arranged" can no longer mean "a trip exists". A
// voucher counts while it is being issued or is usable: ISSUING / CODES_PENDING, or
// ACTIVE before its window ends. A program carrying `lastError` does NOT count — the
// drawer keeps prompting "Organizar el traslado" and the voucher section shows the
// error, so a failed issuance never reads like a successful one.
export function voucherCoversTransport(
  voucher: GroupVoucherView | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!voucher || voucher.lastError) return false;
  if (voucher.status === 'ISSUING' || voucher.status === 'CODES_PENDING') return true;
  return voucher.status === 'ACTIVE' && now <= voucher.endsAt;
}

/** The car legs each scenario pays for — transport's `VOUCHER_LEGS` (one transport per leg). */
const VOUCHER_LEGS: Readonly<Record<VoucherScenario, readonly TripLeg[]>> = {
  HOTEL_OVERNIGHT: ['AIRPORT_TO_HOTEL', 'HOTEL_TO_AIRPORT'],
  RIDE_HOME: ['AIRPORT_TO_HOME'],
  ONE_WAY: ['AIRPORT_TO_HOTEL'],
};

/**
 * The passengers the voucher carries — the voucher IS their ground transport, used
 * whenever they want. Once codes exist: each passenger holding a code that is not
 * cancelled (#826 frees one passenger, the program lives on). While the program is
 * still being issued: its passengers. Empty when the voucher does not count.
 */
export function voucherPassengers(
  voucher: GroupVoucherView | null | undefined,
  now: number = Date.now(),
): ReadonlySet<string> {
  if (!voucher || !voucherCoversTransport(voucher, now)) return new Set();
  if (voucher.assignments.length > 0) {
    return new Set(voucher.assignments.filter((a) => !a.cancelledAt).map((a) => a.passengerUrn));
  }
  return new Set(voucher.passengerUrns);
}

/** Does the voucher pay `passengerUrn`'s ride on `leg`? A car there is a double spend. */
export function voucherCoversRide(
  voucher: GroupVoucherView | null | undefined,
  leg: TripLeg,
  passengerUrn: string,
  now: number = Date.now(),
): boolean {
  if (!voucher || !VOUCHER_LEGS[voucher.scenario]?.includes(leg)) return false;
  return voucherPassengers(voucher, now).has(passengerUrn);
}

/**
 * The roster's voucher fact (`roster.transportVoucher@v1`) — for the lists that read the
 * roster without the drawer's per-group voucher read. Live programs only (cases drops a
 * CANCELLED one); an ACTIVE program past its window no longer counts.
 */
export function rosterVoucherCounts(
  voucher: RosterTransportVoucher | undefined,
  now: number = Date.now(),
): voucher is RosterTransportVoucher {
  if (!voucher) return false;
  if (voucher.status === 'ISSUING' || voucher.status === 'CODES_PENDING') return true;
  return voucher.status === 'ACTIVE' && now <= voucher.endsAt;
}

/**
 * Does the group's voucher take EVERY passenger to the hotel? Infants ride on a lap and
 * never get a code; an empty list on a program still being issued speaks for the group.
 * One code cancelled on its own leaves that passenger without transport — not covered.
 */
export function rosterVoucherCoversGroup(
  row: Pick<DisruptionPnrGroupRow, 'transportVoucher' | 'passengers'>,
  now: number = Date.now(),
): boolean {
  const voucher = row.transportVoucher;
  if (!rosterVoucherCounts(voucher, now)) return false;
  if (voucher.scenario !== 'HOTEL_OVERNIGHT' && voucher.scenario !== 'ONE_WAY') return false;
  if (voucher.paxUrns.length === 0) return voucher.status !== 'ACTIVE';
  const carried = new Set(voucher.paxUrns);
  const seated = row.passengers.filter((p) => !isInfant(p.paxType));
  return seated.length > 0 && seated.every((p) => carried.has(p.paxUrn));
}

function isInfant(paxType: string | undefined): boolean {
  const t = (paxType ?? '').toUpperCase();
  return t === 'INF' || t === 'INFANT';
}
